import React, { useCallback, useState } from 'react';
import cn from 'clsx';
import Swiper from 'swiper';
import { createElementKey } from 'utils';
import { analyticsUtils, ClickButtonElementName } from 'utils/analytics-utils';
import { ModalMediaCard } from '../modal-media-card';
import { SwiperWithNavigation, SwiperSlide } from '../swiper';
import { modalMediaCardGallerySlide, modalMediaCardGallerySwiper } from './modal-media-card-gallery-styles';

export type ModalMediaCardStructure<T> = {
  title: string;
} & T;

type ModalMediaCardGalleryProps<T> = {
  cardList: ModalMediaCardStructure<T>[];
  className?: string;
  elementBlock: string;
  isOpen: boolean;
  onClose: () => void;
  onCardChange: (cardIndex: number) => void;
  openCardIndex: number | undefined;
  renderContent: (card: ModalMediaCardStructure<T>) => JSX.Element;
};

export const ModalMediaCardGallery = <T,>({
  cardList,
  className,
  elementBlock,
  isOpen,
  onClose,
  onCardChange,
  openCardIndex,
  renderContent,
}: ModalMediaCardGalleryProps<T>): JSX.Element => {
  const [isCardClicked, setIsCardClicked] = useState(true);

  const handleOnClose = useCallback(() => {
    onClose();
    analyticsUtils.clickButtonEvent(
      elementBlock,
      'Закрыть',
      `${cardList[openCardIndex || 0].title} / modal`
    );
    setIsCardClicked(true);
  }, [cardList, elementBlock, onClose, openCardIndex]);

  /**
   * Обработчик нажатия кнопки "Следующий слайд"
   */
  const handleNextSlideBtnClick = useCallback(
    ({ activeIndex, params }: Swiper) => {
      onCardChange(activeIndex);
      // Предотвращает срабатывание события GA при первом открытии карточки c индексом > 0
      if (!params.enabled || isCardClicked) {
        setIsCardClicked(false);
        return;
      }

      analyticsUtils.clickButtonEvent(
        elementBlock,
        ClickButtonElementName.SwitchForward,
        `${cardList[activeIndex].title} / modal`
      );
    },
    [isCardClicked, onCardChange, cardList, elementBlock]
  );

  /**
   * Обработчик нажатия кнопки "Предыдущий слайд"
   */
  const handlePrevSlideBtnClick = useCallback(
    ({ activeIndex }: Swiper) => {
      onCardChange(activeIndex);
      analyticsUtils.clickButtonEvent(
        elementBlock,
        ClickButtonElementName.SwitchBackward,
        `${cardList[activeIndex].title} / modal`
      );
    },
    [onCardChange, cardList, elementBlock]
  );

  return (
    <ModalMediaCard show={isOpen} onClose={handleOnClose}>
      <SwiperWithNavigation
        className={cn(modalMediaCardGallerySwiper, className)}
        breakpoints={{
          320: { enabled: false },
          1024: { enabled: true },
        }}
        initialSlide={openCardIndex}
        onSlideNextTransitionEnd={handleNextSlideBtnClick}
        onSlidePrevTransitionEnd={handlePrevSlideBtnClick}>
        {cardList.map((card, index: number) => (
          <SwiperSlide
            className={modalMediaCardGallerySlide}
            key={createElementKey('storage-styles-card', index)}>
            {index === openCardIndex && renderContent(card)}
          </SwiperSlide>
        ))}
      </SwiperWithNavigation>
    </ModalMediaCard>
  );
};

ModalMediaCardGallery.defaultProps = {
  className: '',
};
